import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import contentService from '../services/contentService'
import { Button } from '../components/ui/button'
import { Card, CardContent } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { useToast } from '../hooks/use-toast'
import { Bookmark, Play, Trash2, Film } from 'lucide-react'

const Watchlist = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [watchlist, setWatchlist] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (user) loadWatchlist()
  }, [user])

  const loadWatchlist = async () => {
    setIsLoading(true)
    try {
      const data = await contentService.getWatchlist(user.id)
      setWatchlist(data)
    } catch (error) {
      console.error('Error loading watchlist:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemove = async (workId, title) => {
    const success = await contentService.removeFromWatchlist(user.id, workId)
    if (success) {
      setWatchlist(prev => prev.filter(w => w.id !== workId))
      toast({ title: 'Removed', description: `"${title}" removed from your watchlist.` })
    } else {
      toast({ title: 'Error', description: 'Failed to remove from watchlist.', variant: 'destructive' })
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <Bookmark className="w-16 h-16 mx-auto text-muted-foreground" />
            <h2 className="text-xl font-semibold">Sign in to see your watchlist</h2>
            <Button onClick={() => navigate('/auth')} className="w-full">Sign In</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Bookmark className="w-8 h-8" />
              My Watchlist
            </h1>
            <p className="text-muted-foreground mt-1">{watchlist.length} title{watchlist.length !== 1 ? 's' : ''} saved</p>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="aspect-[2/3] bg-muted rounded-xl animate-pulse" />
            ))}
          </div>
        ) : watchlist.length === 0 ? (
          <div className="text-center py-20">
            <Bookmark className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h2 className="text-xl font-semibold mb-2">Your watchlist is empty</h2>
            <p className="text-muted-foreground mb-6">Browse content and save titles you want to watch later.</p>
            <Button onClick={() => navigate('/browse')}>Browse Content</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {watchlist.map((work) => (
              <div key={work.id} className="group relative">
                <div
                  className="relative aspect-[2/3] bg-muted rounded-xl overflow-hidden cursor-pointer"
                  onClick={() => navigate(`/watch/${work.slug}`)}
                >
                  {work.thumbnail ? (
                    <img
                      src={work.thumbnail}
                      alt={work.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Film className="w-12 h-12 text-muted-foreground" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-semibold text-lg leading-tight">{work.title}</h3>
                    <div className="flex items-center gap-2 mt-2">
                      {work.type && (
                        <Badge variant="secondary" className="text-xs">
                          {work.type === 'tv_show' ? 'TV Show' : 'Movie'}
                        </Badge>
                      )}
                      {work.tags?.[0] && (
                        <Badge variant="outline" className="text-xs text-white border-white/30">
                          {work.tags[0]}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {/* Hover overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                    <Play className="w-14 h-14 text-white" />
                  </div>
                </div>
                {/* Remove button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 bg-black/50 hover:bg-destructive text-white h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemove(work.id, work.title)
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default Watchlist
